import { alpha } from '@mui/material';
import { createTheme } from '@mui/material/styles';

/**  Important Note:
 *
 *  These colors, typography as well as breakpoints below should be mostly
 *  consistent with the scss variables in the shared styles folder.
 *
 **/
const colors = {
    colorBlueDark: '#053361',
    colorBlueDark700: '#0559ad',
    colorBlueLight: '#1773cf',
    colorGreyLight: '#545b63',
    colorYellow: '#ffd900',
    colorYellowAccent: '#fcec3f',
    colorWhite: '#fff',
};

const typography = {
    $carmaxBodyFont:
        'Lato, Corbel, Lucida Grande, Lucida Sans Unicode, Dejavu Sans, Bitstream Vera Sans, Liberation Sans, Verdana, Verdana Ref, sans-serif',
    $carmaxTitleFontBold:
        'CarMaxSharpSansDisp-Bold, Frutiger, Frutiger Linotype, Univers, Calibri, Gill Sans, Gill Sans MT, Myriad Pro, Myriad, DejaVu Sans Condensed, Liberation Sans, Nimbus Sans L, Tahoma, Geneva, Helvetica Neue, Helvetica, Arial, sans-serif',
};

const theme = {
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1650,
        },
    },
    palette: {
        primary: {
            main: colors.colorBlueDark,
        },
        secondary: {
            main: colors.colorYellow,
        },
        text: {
            primary: colors.colorBlueDark,
            primaryLight: colors.colorBlueLight,
            secondary: colors.colorBlueDark,
            neutral: colors.colorGreyLight,
        },
    },
    typography: {
        allVariants: {
            fontFamily: typography.$carmaxBodyFont,
        },
        h4: {
            fontFamily: typography.$carmaxTitleFontBold,
        },
        fontSize: 14,
        button: {
            textTransform: 'uppercase',
        },
    },
    zIndex: {
        // Qualtrics Feedback has a z-index of 2000000000
        modal: 2100000001,
        drawer: 2100000002,
        snackbar: 3100000000,
    },
    components: {
        MuiSnackbar: {
            defaultProps: {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                },
            },
            styleOverrides: {
                root: {
                    '&.MuiSnackbar-root': {
                        zIndex: 3100000000,
                    },
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                disableGutters: {
                    margin: 0,
                    width: '100%',
                    maxWidth: 'initial',
                    ['@media (min-width:600px)']: {
                        maxWidth: 'initial',
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    color: colors.colorGreyLight,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                outlined: true,
            },
            root: {
                borderRadius: '8px',
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {},
                containedPrimary: {
                    backgroundColor: colors.colorYellow,
                    borderRadius: '8px',
                    height: '48px',
                    color: colors.colorBlueDark,
                    fontWeight: '700',
                    '&:hover': {
                        backgroundColor: colors.colorYellowAccent,
                        boxShadow: 'none',
                    },
                    '&:focus': {
                        outline: 'none',
                        boxShadow: 'none',
                        outlineColor: colors.colorBlueLight,
                        outlineStyle: 'auto',
                        outlineWidth: 'initial',
                    },
                },
                text: {
                    borderRadius: '8px',
                    color: colors.colorBlueDark,
                    minWidth: 150,
                    fontSize: 14,
                    fontWeight: '700',
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    fontFamily: typography.$carmaxBodyFont,
                    overflow: 'hidden',
                    borderStyle: 'solid',
                    borderColor: colors.colorBlueDark700,
                    borderWidth: '1px',
                    borderRadius: '4px',
                },
            },
        },
        MuiToggleButton: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    width: '100%',
                    color: colors.colorBlueDark700,
                    fontWeight: '700',
                    backgroundColor: colors.colorWhite,
                    '&:hover': {
                        backgroundColor: alpha(colors.colorBlueDark700, 0.1),
                    },
                    '&.Mui-selected': {
                        backgroundColor: colors.colorBlueDark700,
                        color: colors.colorWhite,
                        '&:hover': {
                            backgroundColor: colors.colorBlueDark700,
                            color: colors.colorWhite,
                        },
                    },
                },
                text: {
                    fontSize: 16,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-checked': {
                        color: colors.colorBlueLight,
                    },
                    '&.MuiCheckbox-indeterminate': {
                        color: colors.colorBlueLight,
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    '&.MuiAccordion-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        borderBottom: '1px solid #C8CACC',
                        borderTop: 0,
                        borderRadius: 0,
                    },
                    '&.Mui-expanded': {
                        margin: 0,
                        paddingBottom: '8px',
                        borderBottom: '1px solid #C8CACC',
                        borderTop: 0,
                        borderRadius: 0,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    '&.MuiAccordionSummary-root': {
                        justifyContent: 'space-between',
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        borderTop: 0,
                    },
                    '&:hover': {
                        backgroundColor: alpha(colors.colorBlueLight, 0.1),
                    },
                    '&.Mui-expanded': {
                        minHeight: '48px',
                        borderTop: 0,
                    },
                },
                content: {
                    margin: '4px 0 0 0',
                    '&.Mui-expanded': {
                        margin: '4px 0 0 0',
                    },
                },
                expandIconWrapper: {
                    '&.MuiAccordionSummary-expandIconWrapper': {
                        color: colors.colorBlueDark,
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    '&.MuiAccordionDetails-root': {
                        padding: '0px',
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: colors.colorBlueLight,
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                variant: 'standard',
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        fontWeight: 'bold',
                        backgroundColor: colors.colorWhite,
                    },
                    '&.Mui-focused': {
                        backgroundColor: colors.colorWhite,
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                root: {},
                paper: {
                    margin: '16px',
                    maxWidth: '568px',
                    width: '100%',
                    ['@media (max-width:450px)']: {
                        margin: '0px',
                    },
                },
                container: {
                    ['@media (max-width:450px)']: {
                        alignItems: 'end',
                    },
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '0px',
                },
            },
        },
        MuiChip: {
            defaultProps: {
                color: 'primary',
            },
            styleOverrides: {
                root: {
                    '&.MuiChip-colorPrimary': {
                        color: colors.colorWhite,
                        backgroundColor: colors.colorBlueLight,
                    },
                    '&.MuiChip-colorPrimary:focus': {
                        boxShadow: '0 0 0 2px #fff, 0 0 0 3px #1773cf',
                    },
                    '&.MuiChip-colorPrimary:hover': {
                        color: colors.colorWhite,
                        backgroundColor: '#0559ad',
                    },
                    '& .MuiChip-deleteIconColorPrimary': {
                        color: colors.colorWhite,
                    },
                    '&.MuiChip-colorSecondary': {
                        color: colors.colorBlueDark,
                        backgroundColor: '#f0f1f2',
                    },
                    '&.MuiChip-colorSecondary:focus': {
                        boxShadow: '0 0 0 2px #fff, 0 0 0 3px #616161',
                    },
                    '& .MuiChip-deleteIconColorSecondary': {
                        color: colors.colorBlueDark,
                    },
                    '&.MuiChip-colorSecondary:hover': {
                        color: colors.colorBlueDark,
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                    '&.MuiChip-colorInfo': {
                        fontWeight: '500',
                        color: '#005ab8',
                        backgroundColor: colors.colorWhite,
                    },
                    '&.MuiChip-colorInfo:focus': {
                        boxShadow: '0 0 0 2px #fff, 0 0 0 3px #1773cf',
                    },
                    '&.MuiChip-outlinedInfo': {
                        borderColor: '#dedfe0',
                    },
                    '& .MuiChip-deleteIconColorInfo': {
                        color: '#005ab8',
                        fontSize: '15px',
                        borderRadius: '50%',
                    },
                    '& .MuiChip-deleteIconColorInfo:hover': {
                        transform: 'scale(1.1)',
                    },
                },
            },
        },
    },
} as const;

type CustomTheme = {
    [Key in keyof typeof theme]: typeof theme[Key];
};
declare module '@mui/material/styles' {
    type theme = CustomTheme;
    type ThemeOptions = CustomTheme;
}

const carmaxTheme = createTheme(theme);

export default carmaxTheme;
export { colors, theme };
